import { httpService } from '@/services/HttpService';
import type { AdminOrderDetail, UpdateOrderStatusRequest } from '../../domain/entities/AdminOrder';
import type {
  OrderDetail,
  OrderFilters,
  OrderListResult,
  DownloadHistoryItem,
  PurchasedGalleryListResult,
} from '../../domain/entities/Order';
import type { OrderRepository } from '../../domain/repositories/OrderRepository';
import type {
  OrderListResponseDTO,
  OrderDetailResponseDTO,
  DownloadHistoryResponseDTO,
  PurchasedGalleryListResponseDTO,
} from '../../application/dtos/OrderDTO';
import { OrderMapper } from '../mappers/OrderMapper';
import { AdminOrderMapper } from '../mappers/AdminOrderMapper';
import type { AdminOrderApi } from '../mappers/AdminOrderMapper';
import type { ApiError } from '@/types';

export class HttpOrderRepository implements OrderRepository {
  private readonly baseUrl = '/user/orders';
  private readonly adminUrl = '/admin/orders';

  async getOrders(filters: OrderFilters = {}, page = 1): Promise<OrderListResult> {
    const params = new URLSearchParams();

    if (filters.status) params.append('status', filters.status);
    if (filters.dateFrom) params.append('date_from', filters.dateFrom);
    if (filters.dateTo) params.append('date_to', filters.dateTo);
    if (filters.perPage) params.append('per_page', filters.perPage.toString());
    params.append('page', page.toString());

    const response = await httpService.get<OrderListResponseDTO>(
      `${this.baseUrl}?${params.toString()}`
    );

    return {
      orders: response.data.map(OrderMapper.toOrderSummary),
      meta: {
        currentPage: response.meta.current_page,
        from: response.meta.from,
        lastPage: response.meta.last_page,
        perPage: response.meta.per_page,
        to: response.meta.to,
        total: response.meta.total,
      },
      links: response.links,
    };
  }

  async getOrderById(orderId: string): Promise<OrderDetail> {
    const response = await httpService.get<OrderDetailResponseDTO>(
      `${this.baseUrl}/${orderId}`
    );

    return OrderMapper.toOrderDetail(response.data);
  }

  // Admin methods (TK116)
  async getAdminOrderDetail(orderId: string): Promise<AdminOrderDetail> {
    const response = await httpService.get<{ data: AdminOrderApi }>(
      `${this.adminUrl}/${orderId}`
    );
    return AdminOrderMapper.toDomain(response.data);
  }

  async updateOrderStatus(
    orderId: string,
    data: UpdateOrderStatusRequest
  ): Promise<AdminOrderDetail> {
    const response = await httpService.patch<{ data: AdminOrderApi }>(
      `${this.adminUrl}/${orderId}/status`,
      data
    );
    return AdminOrderMapper.toDomain(response.data);
  }

  async downloadItem(downloadUrl: string): Promise<void> {
    const token = httpService.getAuthToken();
    const response = await fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const apiError: ApiError = {
        status: response.status,
        data: errorData,
      };

      httpService.handleMaintenanceError(apiError);

      const message = typeof errorData.message === 'string' ? errorData.message : 'Download failed';
      throw new Error(message);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'download.jpg';
    a.click();
    window.URL.revokeObjectURL(url);
  }

  async getDownloadHistory(): Promise<DownloadHistoryItem[]> {
    const response = await httpService.get<DownloadHistoryResponseDTO>('/download/history');
    return response.data.map(OrderMapper.toDownloadHistoryItem);
  }

  async getPurchasedGalleries(page = 1, perPage = 15, slug?: string): Promise<PurchasedGalleryListResult> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());

    if (slug) {
      params.append('slug', slug);
    }

    const response = await httpService.get<PurchasedGalleryListResponseDTO>(
      `/user/galleries?${params.toString()}`
    );

    return {
      galleries: response.data.map(OrderMapper.toPurchasedGallerySummary),
      meta: {
        currentPage: response.meta.current_page,
        from: response.meta.from,
        lastPage: response.meta.last_page,
        perPage: response.meta.per_page,
        to: response.meta.to,
        total: response.meta.total,
      },
      links: response.links,
    };
  }
}
